import { Event, EventCategory } from "@prisma/client";
import Tooltip from "@reach/tooltip";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";
import { NavLink } from "remix";
import { useFestival } from "~/utils/useFestival";
import { Category } from "./Category";
import { IconLiveStream } from "./IconLiveStream";

interface EventLinkOwnProps {
  event: Pick<
    Event,
    "slug" | "title" | "from" | "to" | "external" | "hybrid"
  > & {
    categories: EventCategory[];
  };
  className?: string;
}

export function EventLink({ event, className }: EventLinkOwnProps) {
  const iconWrapperClassName = event.external
    ? "bg-gray-50 group-hover:bg-gray-100 shadow-sm group-hover:shadow-md"
    : "bg-white shadow-md group-hover:bg-primary-400";
  const iconClassName = event.external
    ? "text-gray-500 "
    : "text-primary-500 group-hover:text-white";

  const festival = useFestival();

  return (
    <NavLink
      prefetch="intent"
      to={`/${festival?.slug}/events/${event.slug}`}
      className={({ isActive }) => {
        const base = `relative group p-4 block my-3 rounded-md border-2 ${className}`;

        if (isActive) {
          // return `${base} border-transparent shadow-md border-primary-300 shadow-primary-200 text-primary-800`;
          return `${base} border-transparent shadow-md bg-primary-500 text-primary-50`;
        }

        if (event.external) {
          return `${base} text-gray-700 border-gray-50 bg-transparent hover:border-gray-200 hover:bg-gray-100`;
        }

        return `${base} text-primary-700 border-primary-50 bg-white hover:border-primary-400 hover:bg-primary-50`;
      }}
    >
      {({ isActive }) => (
        <>
          <strong
            className={`text-md md:text-lg font-bold ${
              isActive
                ? "text-white group-hover:text-primary-50 "
                : "text-primary-700 group-hover:text-primary-800"
            }`}
          >
            {event.title}
          </strong>
          <br />
          <span
            className={`text-xs md:text-sm ${
              isActive
                ? "text-primary-50 group-hover:text-primary-100"
                : "text-primary-400 group-hover:text-primary-600"
            }`}
          >
            {format(parseISO(event.from.toString()), "HH:mm", {
              locale: de,
            })}
            {" - "}
            {format(parseISO(event.to.toString()), "HH:mm", {
              locale: de,
            })}
            {" Uhr"}
          </span>
          {(!!event.categories || event.external) && (
            <div className="flex flex-row justify-start mt-2">
              {event.categories?.map((category) => (
                <Category
                  key={category.id}
                  category={category}
                  small
                  external={event.external}
                />
              )) ?? null}
            </div>
          )}
          {event.hybrid && (
            <Tooltip
              label="Hybrid Veranstaltung"
              aria-label="Dies ist eine hybride Veranstaltung"
            >
              <div
                className={`absolute -right-2 -top-2 rounded-full p-1 ${iconWrapperClassName}`}
              >
                <IconLiveStream className={`text-lg ${iconClassName}`} />
              </div>
            </Tooltip>
          )}
        </>
      )}
    </NavLink>
  );
}
